import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    MessageSquareText, RefreshCw, Loader2, AlertCircle, CheckCircle2, Save, Send, RotateCcw, Zap, FileText,
    Users, UserRound, Crown, Receipt, CalendarClock, ChevronRight, Lock, Building2, ChevronDown, Stethoscope, XCircle, AlertTriangle,
} from 'lucide-react';
import { useStore } from '../../../context/StoreContext';
import { SMIIFY_API } from '../../../config/api';
import styles from './WhatsAppMessages.module.css';

/**
 * WhatsApp messages: which approved template goes out for each moment (day closing, bill, feedback,
 * appointments for client and staff, memberships, packages, balance), with automatic sending,
 * variable mapping, a live preview and a test send.
 * HQ settings apply to every branch that has none of its own.
 */
const API = `${SMIIFY_API}/integrations/whatsapp/messages`;
const LANG = { en: 'English', en_US: 'English (US)', en_GB: 'English (UK)', hi: 'Hindi', mr: 'Marathi', gu: 'Gujarati', ta: 'Tamil', te: 'Telugu', bn: 'Bengali', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi' };
const GROUP_ICON = { Daily: CalendarClock, Billing: Receipt, 'Appointments (client)': UserRound, 'Appointments (staff)': Users, Loyalty: Crown };
const AUDIENCE = { OWNER: 'Owner', CLIENT: 'Client', STAFF: 'Staff' };
const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const blank = { enabled: false, templateName: '', templateLang: '', varMap: {}, options: {} };

/* ---------- the same rules the server uses to fill a template ---------- */
const needsOf = (tpl) => {
    const comps = tpl?.components || [];
    const body = comps.find((c) => c.type === 'BODY')?.text || '';
    const header = comps.find((c) => c.type === 'HEADER');
    const buttons = comps.find((c) => c.type === 'BUTTONS')?.buttons || [];
    const vars = [...new Set([...body.matchAll(/\{\{\s*([^}\s]+)\s*\}\}/g)].map((m) => m[1]))];
    const named = vars.some((v) => !/^\d+$/.test(v));
    return {
        body, header, buttons, named,
        vars: named ? vars : vars.map(Number).sort((a, b) => a - b).map(String),
        headerVar: header?.format === 'TEXT' && /\{\{/.test(header.text || ''),
        headerName: header?.format === 'TEXT' ? ((String(header.text || '').match(/\{\{\s*([^}\s]+)\s*\}\}/) || [])[1] || '') : '',
        headerMedia: ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(header?.format) ? header.format : '',
        urlButton: buttons.find((b) => b.type === 'URL' && /\{\{/.test(b.url || '')) || null,
    };
};
const autoField = (ev, v) => {
    if (v === 'header') return 'salon_name';
    if (v === 'button') return ev.button;
    if (/^\d+$/.test(v)) return ev.order[Number(v) - 1] || null;
    const n = norm(v);
    const f = ev.fields.find((x) => norm(x.key) === n) || ev.fields.find((x) => (x.aliases || []).some((a) => norm(a) === n));
    return f ? f.key : null;
};
const sampleOf = (ev, mapping, v, afterRupee) => {
    const m = mapping[v] || '';
    let out;
    if (m.startsWith('text:')) out = m.slice(5) || '…';
    else {
        const key = m.startsWith('field:') ? m.slice(6) : autoField(ev, v);
        out = ev.fields.find((f) => f.key === key)?.sample ?? '-';
    }
    return afterRupee ? String(out).replace(/^\u20B9\s*/, '') : out;
};

const statusOf = (s) => {
    const e = s?.effective || blank;
    if (s?.locked) return { text: e.templateName ? 'Locked by HQ' : 'Locked · not set', tone: 'hq' };
    if (!e.templateName) return { text: 'Not set', tone: 'off' };
    if (s.source === 'HQ' && !s.own) return { text: e.enabled ? 'Auto · HQ' : 'Template · HQ', tone: e.enabled ? 'hq' : 'muted' };
    return e.enabled ? { text: 'Auto', tone: 'on' } : { text: 'Template only', tone: 'muted' };
};

/**
 * <WhatsAppMessages />                                  own page (Integrations > WhatsApp messages)
 * <WhatsAppMessages embedded initialEvent="bill" onSummary={(s) => ...} />   inside Settings
 */
export default function WhatsAppMessages({ embedded = false, initialEvent = '', onSummary = null }) {
    const { activeStore } = useStore();
    const isHQ = !activeStore || activeStore.isHQ === true || [activeStore.id, activeStore._id].includes('global_hq');
    const storeId = isHQ ? 'global_hq' : (activeStore?._id || activeStore?.id);
    const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('smiify_token')}`, 'x-store-id': storeId }), [storeId]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(initialEvent || 'day_closing');
    const [draft, setDraft] = useState(blank);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testPhone, setTestPhone] = useState('');
    const [testing, setTesting] = useState(false);
    const [toast, setToast] = useState(null);
    const [policyDraft, setPolicyDraft] = useState(null);   // HQ: branch access being edited
    const [policyOpen, setPolicyOpen] = useState(false);
    const [policySaving, setPolicySaving] = useState(false);
    const [check, setCheck] = useState(null);          // why it is (not) sending
    const [checking, setChecking] = useState(false);
    const [checkBranch, setCheckBranch] = useState('');
    const [applying, setApplying] = useState(false);

    const flash = (message, tone = 'ok') => { setToast({ message, tone }); setTimeout(() => setToast(null), 4500); };

    const load = useCallback(async (fresh = false) => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API}${fresh ? '?fresh=1' : ''}`, { headers });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || `Could not load (error ${res.status}).`);
            setData(j.data);
            if (j.data.isHQ && j.data.policy) setPolicyDraft({ branchCanChoose: j.data.policy.branchCanChoose !== false, branchOverrides: { ...(j.data.policy.branchOverrides || {}) }, lockedEvents: [...(j.data.policy.lockedEvents || [])] });
        } catch (e) {
            setError(e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message);
        } finally { setLoading(false); }
    }, [headers]);
    useEffect(() => { load(); }, [load]);

    // a link may name a message that does not exist: fall back to the first one
    useEffect(() => {
        if (data && !data.events.some((e) => e.key === selected)) setSelected(data.events[0]?.key || 'day_closing');
    }, [data, selected]);
    // tell the parent (Settings menu) how many go out automatically
    useEffect(() => {
        if (!data || !onSummary) return;
        const auto = data.events.filter((e) => data.settings[e.key]?.effective?.enabled && data.settings[e.key]?.effective?.templateName).length;
        onSummary({ auto, total: data.events.length });
    }, [data, onSummary]);

    const ev = useMemo(() => data?.events.find((e) => e.key === selected) || null, [data, selected]);
    const current = data?.settings?.[selected];
    // a new moment: forget the last check
    useEffect(() => { setCheck(null); }, [selected]);

    const runCheck = async () => {
        setChecking(true);
        try {
            const q = data?.isHQ && checkBranch ? `?branch=${checkBranch}` : '';
            const res = await fetch(`${API}/${selected}/check${q}`, { headers });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not check.');
            setCheck(j.data);
        } catch (e) { flash(e.message, 'err'); } finally { setChecking(false); }
    };
    const applyAll = async () => {
        if (!window.confirm('Every branch will use the HQ template for this message. Their own choice for it is removed. Continue?')) return;
        setApplying(true);
        try {
            const res = await fetch(`${API}/${selected}/apply-all`, { method: 'POST', headers });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not update the branches.');
            flash(j.message);
            await load();
        } catch (e) { flash(e.message, 'err'); } finally { setApplying(false); }
    };

    // the editor starts from this branch's own settings, else what it follows (HQ)
    useEffect(() => {
        if (!current) return;
        // a message HQ has locked shows what really goes out (HQ's choice), not the branch's old one
        const base = (current.locked ? current.effective : current.own || current.effective) || blank;
        setDraft({ ...blank, ...base, varMap: { ...(base.varMap || {}) }, options: { ...(base.options || {}) } });
        setDirty(false);
    }, [current, selected]);

    const groups = useMemo(() => {
        const g = new Map();
        (data?.events || []).forEach((e) => { if (!g.has(e.group)) g.set(e.group, []); g.get(e.group).push(e); });
        return [...g.entries()];
    }, [data]);

    const names = useMemo(() => [...new Set((data?.templates || []).map((t) => t.name))].sort(), [data]);
    const versions = useMemo(() => (data?.templates || []).filter((t) => t.name === draft.templateName), [data, draft.templateName]);
    const tpl = useMemo(() => versions.find((t) => t.lang === draft.templateLang) || versions[0] || null, [versions, draft.templateLang]);
    const need = useMemo(() => (tpl ? needsOf(tpl) : null), [tpl]);
    const rupeeVars = useMemo(() => new Set((need?.vars || []).filter((v) => new RegExp(`\u20B9[*_~\\s]*\\{\\{\\s*${v}\\s*\\}\\}`).test(need.body))), [need]);
    const lockedHere = Boolean(current?.locked);                 // this branch may not change this message
    const canEdit = Boolean(data?.permissions?.canEdit) && !lockedHere;

    const savePolicy = async () => {
        setPolicySaving(true);
        try {
            const res = await fetch(`${API}/policy`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(policyDraft) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            flash(j.message || 'Saved.');
            await load();
        } catch (e) { flash(e.message, 'err'); } finally { setPolicySaving(false); }
    };
    const policyDirty = Boolean(data?.isHQ && policyDraft && JSON.stringify(policyDraft) !== JSON.stringify({ branchCanChoose: data.policy?.branchCanChoose !== false, branchOverrides: data.policy?.branchOverrides || {}, lockedEvents: data.policy?.lockedEvents || [] }));

    const change = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
    const setMap = (v, value) => { setDraft((d) => { const varMap = { ...d.varMap }; if (value) varMap[v] = value; else delete varMap[v]; return { ...d, varMap }; }); setDirty(true); };
    const pickTemplate = (name) => {
        const first = (data?.templates || []).find((t) => t.name === name);
        change({ templateName: name, templateLang: first?.lang || '', varMap: {} });
    };
    const select = (key) => {
        if (dirty && !window.confirm('You have unsaved changes. Leave them?')) return;
        setSelected(key);
    };

    const save = async () => {
        setSaving(true);
        try {
            const body = { ...draft, templateLang: tpl?.lang || draft.templateLang };
            const res = await fetch(`${API}/${selected}`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not save.');
            flash(j.message || 'Saved.');
            setDirty(false);
            await load();
        } catch (e) { flash(e.message, 'err'); } finally { setSaving(false); }
    };
    const resetToHq = async () => {
        if (!window.confirm('Use the HQ settings for this message in this branch?')) return;
        try {
            const res = await fetch(`${API}/${selected}`, { method: 'DELETE', headers });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j.success) throw new Error(j.message || 'Could not reset.');
            flash(j.message); await load();
        } catch (e) { flash(e.message, 'err'); }
    };
    const test = async () => {
        setTesting(true);
        try {
            const res = await fetch(`${API}/${selected}/test`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ phone: testPhone, setting: { ...draft, templateLang: tpl?.lang || draft.templateLang } }) });
            const j = await res.json().catch(() => ({}));
            flash(j.message || (res.ok ? 'Sent.' : 'Not sent.'), j.success ? 'ok' : 'err');
        } catch (e) { flash(e.message, 'err'); } finally { setTesting(false); }
    };

    /* ---------- preview text ---------- */
    const preview = useMemo(() => {
        if (!need || !ev) return null;
        const fill = (text, varsAfterRupee) => String(text || '').split(/(\{\{\s*[^}\s]+\s*\}\})/g).map((part, i) => {
            const m = part.match(/^\{\{\s*([^}\s]+)\s*\}\}$/);
            if (!m) return <span key={i}>{part}</span>;
            return <mark key={i} className={styles.fill}>{sampleOf(ev, draft.varMap, m[1], varsAfterRupee?.has(m[1]))}</mark>;
        });
        return {
            // a named header variable shows its own value ({{client_name}} -> Priya); a numbered one, the salon name
            header: need.header?.format === 'TEXT' ? (need.headerVar ? fill(/^\d+$/.test(need.headerName) ? String(need.header.text).replace(/\{\{\s*[^}\s]+\s*\}\}/, '{{header}}') : need.header.text) : need.header.text) : null,
            body: fill(need.body, rupeeVars),
            footer: (tpl.components || []).find((c) => c.type === 'FOOTER')?.text || '',
            buttons: need.buttons.map((b) => b.text),
        };
    }, [need, ev, draft.varMap, rupeeVars, tpl]);

    const mappingRows = useMemo(() => {
        if (!need) return [];
        const rows = need.vars.map((v) => ({ v, label: `{{${v}}}` }));
        if (need.headerVar) {
            const hn = need.headerName;
            if (/^\d+$/.test(hn)) rows.push({ v: 'header', label: 'Header' });
            else if (!need.vars.includes(hn)) rows.push({ v: hn, label: `Header {{${hn}}}` });   // same name in the body: one row fills both
        }
        if (need.urlButton) rows.push({ v: 'button', label: `Button "${need.urlButton.text}"` });
        return rows;
    }, [need]);

    if (loading && !data) return <div className={styles.center}><Loader2 size={22} className={styles.spin} /></div>;

    return (
        <div className={`${styles.page} ${embedded ? styles.embedded : ''}`}>
            <header className={styles.top}>
                <div className={styles.titleRow}>
                    <span className={styles.icon}><MessageSquareText size={17} /></span>
                    <div>
                        <h2>WhatsApp messages</h2>
                        <p>{isHQ ? 'HQ settings: every branch without its own uses these.' : `${activeStore?.storeName || 'This branch'}: its own choices, else the HQ settings.`}</p>
                    </div>
                </div>
                <button type="button" className={styles.ghostBtn} onClick={() => load(true)} disabled={loading}>
                    <RefreshCw size={14} className={loading ? styles.spin : ''} /> Refresh templates
                </button>
            </header>

            {toast && <div className={`${styles.toast} ${toast.tone === 'err' ? styles.toastErr : ''}`}>{toast.tone === 'err' ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}<span>{toast.message}</span></div>}
            {error && <div className={`${styles.toast} ${styles.toastErr}`}><AlertCircle size={15} /><span>{error}</span></div>}
            {data?.lineError && <div className={`${styles.toast} ${styles.toastWarn}`}><AlertCircle size={15} /><span>WhatsApp line: {data.lineError} Connect it in Integrations &gt; WhatsApp to choose templates.</span></div>}

            {data?.isHQ && policyDraft && (
                <section className={styles.policy} aria-label="Branch access">
                    <button type="button" className={styles.policyHead} onClick={() => setPolicyOpen((v) => !v)} aria-expanded={policyOpen}>
                        <span className={styles.icon} style={{ width: 30, height: 30 }}><Building2 size={15} /></span>
                        <span className={styles.policyText}>
                            <strong>Branch access</strong>
                            <small>
                                {policyDraft.branchCanChoose ? 'Branches can choose their own templates' : 'Only HQ chooses templates'}
                                {policyDraft.lockedEvents.length ? ` · ${policyDraft.lockedEvents.length} message${policyDraft.lockedEvents.length === 1 ? '' : 's'} HQ only` : ''}
                                {Object.keys(policyDraft.branchOverrides).length ? ` · ${Object.keys(policyDraft.branchOverrides).length} branch exception${Object.keys(policyDraft.branchOverrides).length === 1 ? '' : 's'}` : ''}
                            </small>
                        </span>
                        {policyDirty && <span className={styles.unsaved}>Unsaved</span>}
                        <ChevronDown size={16} style={{ transform: policyOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                    </button>
                    {policyOpen && (
                        <div className={styles.policyBody}>
                            <div className={styles.switchRow}>
                                <div>
                                    <strong>Branches can choose their own templates</strong>
                                    <span>Off: every branch uses the templates chosen here at HQ and cannot change them.</span>
                                </div>
                                <label className={styles.switch}>
                                    <input type="checkbox" checked={policyDraft.branchCanChoose} onChange={(e) => setPolicyDraft({ ...policyDraft, branchCanChoose: e.target.checked })} disabled={!data.permissions?.canEdit} aria-label="Branches can choose their own templates" />
                                    <span />
                                </label>
                            </div>

                            <div className={styles.block}>
                                <span className={styles.blockTitle}>Only HQ chooses these messages</span>
                                <div className={styles.lockChips}>
                                    {data.events.map((e) => {
                                        const on = policyDraft.lockedEvents.includes(e.key);
                                        return (
                                            <button type="button" key={e.key} aria-pressed={on} className={`${styles.lockChip} ${on ? styles.lockChipOn : ''}`} disabled={!data.permissions?.canEdit}
                                                onClick={() => setPolicyDraft({ ...policyDraft, lockedEvents: on ? policyDraft.lockedEvents.filter((k) => k !== e.key) : [...policyDraft.lockedEvents, e.key] })}>
                                                {on && <Lock size={11} />} {e.label}
                                            </button>
                                        );
                                    })}
                                </div>
                                <small className={styles.help}>Even branches that may choose cannot change these.</small>
                            </div>

                            {(data.policy?.branches || []).length > 0 && (
                                <div className={styles.block}>
                                    <span className={styles.blockTitle}>Branches</span>
                                    <div className={styles.branchList}>
                                        {data.policy.branches.map((b) => {
                                            const o = policyDraft.branchOverrides[b.id];
                                            const value = o === true ? 'allow' : o === false ? 'lock' : 'default';
                                            const set = (v) => {
                                                const next = { ...policyDraft.branchOverrides };
                                                if (v === 'default') delete next[b.id]; else next[b.id] = v === 'allow';
                                                setPolicyDraft({ ...policyDraft, branchOverrides: next });
                                            };
                                            const effective = o === undefined ? policyDraft.branchCanChoose : o;
                                            return (
                                                <div key={b.id} className={styles.branchRow}>
                                                    <span className={styles.branchName}>{b.name}<small>{effective ? 'Can choose' : 'Uses HQ templates'}</small></span>
                                                    <div className={styles.seg} role="radiogroup" aria-label={`Access for ${b.name}`}>
                                                        {[['default', 'Default'], ['allow', 'Allowed'], ['lock', 'Locked']].map(([v, l]) => (
                                                            <button type="button" key={v} role="radio" aria-checked={value === v} className={value === v ? styles.segOn : ''} onClick={() => set(v)} disabled={!data.permissions?.canEdit}>{l}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className={styles.saveRow}>
                                <button type="button" className={styles.primaryBtn} onClick={savePolicy} disabled={!policyDirty || policySaving || !data.permissions?.canEdit}>
                                    {policySaving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Save branch access
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            )}

            {data && !data.isHQ && data.policy && !data.policy.canChoose && (
                <div className={`${styles.toast} ${styles.toastWarn}`}><Lock size={15} /><span>HQ manages the WhatsApp templates of this branch. You can see them here; ask HQ to change them.</span></div>
            )}

            {data && (
                <div className={styles.layout}>
                    {/* ---------------- moments ---------------- */}
                    <nav className={styles.list} aria-label="Messages">
                        {groups.map(([group, events]) => {
                            const Icon = GROUP_ICON[group] || FileText;
                            return (
                                <div key={group} className={styles.group}>
                                    <span className={styles.groupLabel}><Icon size={13} /> {group}</span>
                                    {events.map((e) => {
                                        const st = statusOf(data.settings[e.key]);
                                        const name = data.settings[e.key]?.effective?.templateName;
                                        return (
                                            <button type="button" key={e.key} className={`${styles.item} ${selected === e.key ? styles.itemOn : ''}`} onClick={() => select(e.key)} aria-current={selected === e.key ? 'true' : undefined}>
                                                <span className={styles.itemText}>
                                                    <strong>{e.label}</strong>
                                                    <small>{name || 'No template'}</small>
                                                </span>
                                                <span className={`${styles.chip} ${styles[`chip_${st.tone}`]}`}>{st.text}</span>
                                                <ChevronRight size={14} className={styles.chev} />
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </nav>

                    {/* ---------------- editor ---------------- */}
                    {ev && (
                        <section className={styles.editor} aria-label={ev.label}>
                            <div className={styles.edHead}>
                                <div>
                                    <h3>{ev.label} <span className={styles.aud}>to {AUDIENCE[ev.audience]}</span></h3>
                                    <p>{ev.when}</p>
                                </div>
                                {lockedHere && <span className={`${styles.chip} ${styles.chip_hq}`}><Lock size={10} /> Locked by HQ</span>}
                                {!lockedHere && !isHQ && current?.source === 'HQ' && !current.own && <span className={`${styles.chip} ${styles.chip_hq}`}>Following HQ</span>}
                            </div>

                            {lockedHere && <p className={styles.note}>HQ chooses this message for your branch. It goes out with the template below; ask HQ to change it.</p>}
                            {isHQ && (data.branchOwn?.[selected] || []).length > 0 && (
                                <div className={styles.ownNote}>
                                    <AlertTriangle size={14} />
                                    <span><b>{data.branchOwn[selected].length} branch{data.branchOwn[selected].length === 1 ? '' : 'es'}</b> use their own template for this message, not this one: {data.branchOwn[selected].map((b) => b.name).join(', ')}.</span>
                                    {canEdit && <button type="button" className={styles.ghostBtn} onClick={applyAll} disabled={applying}>{applying ? <Loader2 size={13} className={styles.spin} /> : <Building2 size={13} />} Make all branches use this</button>}
                                </div>
                            )}
                            <div className={styles.switchRow}>
                                <div>
                                    <strong><Zap size={14} /> Send automatically</strong>
                                    <span>{ev.key === 'day_closing' ? 'Time and numbers are in EOD settings.' : 'When this moment happens, the client gets this message by itself.'}</span>
                                </div>
                                <label className={styles.switch}>
                                    <input type="checkbox" checked={draft.enabled} onChange={(e) => change({ enabled: e.target.checked })} disabled={!canEdit} aria-label="Send automatically" />
                                    <span />
                                </label>
                            </div>
                            {draft.templateName && !draft.enabled && (
                                <p className={styles.offNote}><AlertTriangle size={13} /> Automatic sending is off: this message goes only when someone presses a Send button. Turn on "Send automatically" to send it by itself.</p>
                            )}

                            <div className={styles.grid2}>
                                <label className={styles.field}>
                                    <span>Template</span>
                                    <select value={versions.length ? draft.templateName : ''} onChange={(e) => pickTemplate(e.target.value)} disabled={!canEdit || !names.length}>
                                        <option value="">{names.length ? 'Choose a template' : 'No approved template'}</option>
                                        {names.map((n) => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </label>
                                <label className={styles.field}>
                                    <span>Version (language)</span>
                                    <select value={tpl?.lang || ''} onChange={(e) => change({ templateLang: e.target.value })} disabled={!canEdit || versions.length < 2}>
                                        {!versions.length && <option value="">—</option>}
                                        {versions.map((v) => <option key={v.lang} value={v.lang}>{LANG[v.lang] || v.lang}{v.category ? ` · ${String(v.category).toLowerCase()}` : ''}</option>)}
                                    </select>
                                </label>
                                {(ev.options || []).map((o) => (
                                    <label key={o.key} className={styles.field}>
                                        <span>{o.label}</span>
                                        <input type="number" min={o.min} max={o.max} value={draft.options?.[o.key] ?? o.default} onChange={(e) => change({ options: { ...draft.options, [o.key]: e.target.value === '' ? '' : Number(e.target.value) } })} disabled={!canEdit} />
                                    </label>
                                ))}
                            </div>

                            {draft.templateName && !versions.length && <p className={styles.warn}>"{draft.templateName}" is not approved on this line any more. Choose another template.</p>}
                            {need?.headerMedia && <p className={styles.warn}>This template has a {need.headerMedia.toLowerCase()} header. Automatic messages need a text header; choose another template.</p>}
                            {tpl && String(tpl.category).toUpperCase() === 'MARKETING' && ev.audience !== 'OWNER' && <p className={styles.note}>This is a Marketing template: it costs more and Meta may hold it back. Utility suits {ev.label.toLowerCase()} best.</p>}

                            {mappingRows.length > 0 && (
                                <div className={styles.block}>
                                    <span className={styles.blockTitle}>Fill the variables</span>
                                    <div className={styles.mapTable}>
                                        {mappingRows.map(({ v, label }) => {
                                            const m = draft.varMap[v] || '';
                                            const auto = autoField(ev, v);
                                            const autoLabel = ev.fields.find((f) => f.key === auto)?.label;
                                            const mode = m.startsWith('text:') ? 'text' : m.startsWith('field:') ? m : '';
                                            return (
                                                <div key={v} className={styles.mapRow}>
                                                    <code>{label}</code>
                                                    <select value={mode} onChange={(e) => setMap(v, e.target.value === 'text' ? 'text:' : e.target.value)} disabled={!canEdit}>
                                                        <option value="">Auto{autoLabel ? `: ${autoLabel}` : ' (nothing matches)'}</option>
                                                        {ev.fields.filter((f) => (v === 'button') === Boolean(f.button) || v === 'header').map((f) => <option key={f.key} value={`field:${f.key}`}>{f.label}</option>)}
                                                        <option value="text">Custom text…</option>
                                                    </select>
                                                    {mode === 'text'
                                                        ? <input value={m.slice(5)} onChange={(e) => setMap(v, `text:${e.target.value}`)} placeholder="Type the text" maxLength={200} disabled={!canEdit} />
                                                        : <span className={styles.sample}>{sampleOf(ev, draft.varMap, v, rupeeVars.has(v))}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {!autoField(ev, mappingRows[0]?.v) && !draft.varMap[mappingRows[0]?.v] && <p className={styles.note}>Some variables do not match a field by name. Choose a field for each, or the message shows “-”.</p>}
                                </div>
                            )}

                            {preview && (
                                <div className={styles.block}>
                                    <span className={styles.blockTitle}>Preview (sample values)</span>
                                    <div className={styles.chat}>
                                        <div className={styles.bubble}>
                                            {preview.header && <strong className={styles.bHead}>{preview.header}</strong>}
                                            <div className={styles.bBody}>{preview.body}</div>
                                            {preview.footer && <small className={styles.bFoot}>{preview.footer}</small>}
                                            {preview.buttons.map((b) => <span key={b} className={styles.bBtn}>{b}</span>)}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className={styles.actions}>
                                <div className={styles.testRow}>
                                    <input value={testPhone} onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} inputMode="numeric" placeholder="Mobile for a test" aria-label="Mobile number for a test" disabled={isHQ} />
                                    <button type="button" className={styles.ghostBtn} onClick={test} disabled={isHQ || testing || testPhone.length !== 10 || !tpl} title={isHQ ? 'Select a branch to send a test' : ''}>
                                        {testing ? <Loader2 size={14} className={styles.spin} /> : <Send size={14} />} Send test
                                    </button>
                                </div>
                                <div className={styles.saveRow}>
                                    {!isHQ && current?.own && !lockedHere && <button type="button" className={styles.linkBtn} onClick={resetToHq} disabled={!canEdit}><RotateCcw size={13} /> Use HQ settings</button>}
                                    {dirty && <span className={styles.unsaved}>Unsaved changes</span>}
                                    {!lockedHere && (
                                        <button type="button" className={styles.primaryBtn} onClick={save} disabled={!canEdit || saving || !dirty}>
                                            {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Save
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* why is it (not) sending? */}
                            <div className={styles.checkBox}>
                                <div className={styles.checkHead}>
                                    <div><strong><Stethoscope size={14} /> Is it going out?</strong><span>Checks this message step by step and shows the last attempts.</span></div>
                                    <div className={styles.checkCtl}>
                                        {isHQ && (data.policy?.branches || []).length > 0 && (
                                            <select value={checkBranch} onChange={(e) => { setCheckBranch(e.target.value); setCheck(null); }} aria-label="Check for">
                                                <option value="">HQ</option>
                                                {data.policy.branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                            </select>
                                        )}
                                        <button type="button" className={styles.ghostBtn} onClick={runCheck} disabled={checking}>{checking ? <Loader2 size={13} className={styles.spin} /> : <Stethoscope size={13} />} {check ? 'Check again' : "Why isn't it sending?"}</button>
                                    </div>
                                </div>
                                {dirty && check && <p className={styles.offNote}><AlertTriangle size={13} /> Save first: the check uses the saved settings.</p>}
                                {check && (
                                    <>
                                        <div className={`${styles.verdict} ${check.ready ? styles.verdictOk : styles.verdictBad}`}>
                                            {check.ready ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                                            {check.ready ? 'Everything is in place: this message goes out.' : 'This message will not go out until the red step is fixed.'}
                                        </div>
                                        <ol className={styles.stepList}>
                                            {check.steps.map((st, i) => (
                                                <li key={i} className={st.ok ? styles.stepOk : styles.stepBad}>
                                                    {st.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                                                    <div><b>{st.title}</b><span>{st.detail}</span></div>
                                                </li>
                                            ))}
                                        </ol>
                                        <span className={styles.blockTitle}>Last attempts</span>
                                        {check.log.length === 0 ? <p className={styles.muted}>No attempts yet for this message{checkBranch || !isHQ ? ' here' : ' at HQ'}.</p> : (
                                            <div className={styles.logList}>
                                                {check.log.map((l) => (
                                                    <div key={l._id} className={styles.logRow}>
                                                        <span className={l.ok ? styles.logOk : l.skipped ? styles.logSkip : styles.logBad}>{l.ok ? 'Sent' : l.skipped ? 'Not sent' : 'Failed'}</span>
                                                        <span>{new Date(l.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                                                        <span>{l.phone ? `••${l.phone}` : ''}{l.manual ? ' · button' : ''}</span>
                                                        <span className={styles.logWhy}>{l.ok ? l.template : l.reason}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            )}
        </div>
    );
}